import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { DashboardCount } from "../types/dashboardCountTypes";

/**
 * Filter-dropdown distributions returned by dashboard-counts unless the call passes
 * include_distributions=false (KPI-card-only callers). They feed FilterDrawer.
 */
export const DISTRIBUTION_KEYS = [
  "notice_period_counts",
  "joining_availability_counts",
  "currency_counts",
  "total_experience_counts",
] as const;

type Distributions = Partial<Pick<DashboardCount, (typeof DISTRIBUTION_KEYS)[number]>>;

export interface CountState {
  count: DashboardCount | null;
  error: string | null;
  loading: boolean | null;
  /** Last distributions received; refills a lean (no-distributions) response so drawer counts never blank. */
  distributions: Distributions | null;
}
const initialState: CountState = {
  count: null,
  error: null,
  loading: false,
  distributions: null,
};

const pickDistributions = (payload: DashboardCount): Distributions | null => {
  const found: Distributions = {};
  let any = false;
  DISTRIBUTION_KEYS.forEach((key) => {
    if (payload && payload[key] !== undefined) {
      (found as Record<string, unknown>)[key] = payload[key];
      any = true;
    }
  });
  return any ? found : null;
};

const countSlice = createSlice({
  name: "count",
  initialState,
  reducers: {
    setCount: (state: CountState, action: PayloadAction<DashboardCount>) => {
      const payload = action.payload as DashboardCount;
      const received = pickDistributions(payload);
      if (received) {
        // Full response: remember its distributions for later lean responses.
        state.distributions = received;
        state.count = payload;
      } else {
        // Lean response (include_distributions=false): keep the last known distribution keys.
        state.count = { ...(state.distributions ?? {}), ...payload } as DashboardCount;
      }
    },
    updateCount: (state: CountState, action: PayloadAction<DashboardCount>) => {
      if (state.count == null) {
        state.count = action.payload as DashboardCount;
      } else {
        state.count = { ...state.count, ...action.payload };
      }
    },
    setCountError: (state: CountState, action: PayloadAction<string>) => {
      state.error = action.payload as string;
    },
    setCountLoading: (state: CountState, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      // Clear stale counts whenever a fresh fetch begins so cards show skeleton instead of old data
      if (action.payload === true) {
        state.count = null;
      }
    },
    resetCountState: () => initialState,
  },
});

export const {
  setCount,
  updateCount,
  setCountError,
  setCountLoading,
  resetCountState,
} = countSlice.actions;
export default countSlice.reducer;
